import { BRAND } from '@/lib/cms/config';

export interface AppointmentPayload {
  salutation?: string;
  phone?: string;
  reason?: string;
  websiteCountryCode?: string;
  languageCode?: string;
  locationId?: string;
  firstName?: string;
  lastName?: string;
  firstNameKanji?: string;
  lastNameKanji?: string;
  email?: string;
  comments?: string;
  reserveProductSku?: string | null;
  startDateTime?: string;
  newsletterOptIn?: boolean;
  source?: string;
  /** reCAPTCHA token — forwarded to POS Manager as recaptchaResponse for server-side verification. */
  recaptchaResponse?: string | null;
}

export interface AppointmentResponse {
  success: boolean;
  appointmentId?: string;
  type?: string;
  message?: string;
}

/**
 * Submit an appointment booking request via the BFF API.
 *
 * Example Input:
 * { locationId: "123", email: "user@example.com", startDateTime: "2026-06-01T10:00:00+00:00" }
 *
 * Example Output:
 * { success: true, appointmentId: "abc-123" }
 */
export async function createAppointment(payload: AppointmentPayload): Promise<AppointmentResponse> {
  const response = await fetch('/api/appointment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      brand: BRAND.toUpperCase(),
    }),
  });

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return {
      success: false,
      message: response.ok
        ? 'Invalid response from appointment service'
        : 'Failed to submit appointment',
    };
  }

  if (!response.ok) {
    const errorBody = body as { message?: string; error?: string };
    return {
      success: false,
      message: errorBody.message ?? errorBody.error ?? 'Failed to submit appointment',
    };
  }

  return body as AppointmentResponse;
}
